import { Formik } from "formik";
import { get, isEmpty } from "lodash-es";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { apiShape, useNotifier, withApi } from "../../context";
import ProductRules from "../../rules/ProductRules";
import CompanyTypeahead from "../Companies/CompanyTypeahead";
import DateTimeInput from "../DateTimeInput";
import {
  ButtonGroup,
  CancelButton,
  Form,
  SelectBox,
  SubmitButton,
  TextArea,
  TextInput,
} from "../Form";
import ProductCategoryTypeahead from "../Products/ProductCategoryTypeahead";
import ProductTypeahead from "../Products/ProductTypeahead";
import TenantTypeahead from "../Tenants/TenantTypeahead";
import AssetStatusSelect from "./AssetStatusSelect";

import { getProductCodeForRules, isSomething } from "../../utils";
import { SubClue } from "../Clue/SubClue";
import SwitchInput from "../Form/SwitchInput";
import SpecificationsInput from "../Specifications/SpecificationsInput";
import AssetFormSynchronization from "./AssetFormSynchronization";
import AssetGPSSettingsHelp from "./AssetGPSSettingsHelp";

import { isUserInGroup } from "../../extractors";
import { useUser } from "../../hooks";
import { validateSpecifications } from "../../validators";
import { PROJECT_ENGINEER_ROLE } from "../Specifications/SpecificationConstants";
import { FormValuesType } from "./types";

type OwnProps = {
  api: apiShape;
  onSubmit: $TSFixMeFunction;
  onChange: $TSFixMeFunction;
  showAssetStatus?: boolean;
  initialValues?: FormValuesType;
  gpsInterval?: {
    id?: string;
    name?: string;
  };
  canSubmit?: boolean;
  gpsDevSettings?: boolean;
};

const defaultProps: Partial<OwnProps> = {
  initialValues: undefined,
  showAssetStatus: false,
  canSubmit: true,
};

type Props = OwnProps & typeof defaultProps;

export function AssetForm({
  onSubmit,
  initialValues,
  showAssetStatus,
  api,
  onChange,
  canSubmit,
  gpsDevSettings,
}: Props) {
  const intl = useIntl();
  const notifier = useNotifier();
  const user = useUser();

  const [userAdminTenants, setUserAdminTenants] = useState();
  const [gpsError, setGPSError] = useState(null);

  const isUserProjectEngineer = isUserInGroup(user, PROJECT_ENGINEER_ROLE);

  React.useEffect(() => {
    const loadData = async () => {
      try {
        const response = await api.getAll("/tenants", {
          admin_user_id: user?.id,
        });
        setUserAdminTenants(response.tenants);
      } catch (error) {
        notifier.showError(api.translateError(error));
      }
    };

    if (user?.id) {
      loadData();
    }
  }, [user?.id]);

  const validateForm = (values: $TSFixMe) => {
    /* this hack is needed to trigger the changes of values to the parent component of connected assets clue component (which check addon limits).
     * Alternatively these changes can be implemented: the clue component can be moved in the form and shown if needed */
    onChange(values);

    const errors = {};

    if (!values.manufacturer) {
      (errors as $TSFixMe).manufacturer = intl.formatMessage({
        id: "validation.manufacturer.mandatory",
      });
    }

    if (
      values.manufacturer?.new &&
      !values.tenant &&
      (userAdminTenants as $TSFixMe)?.length > 1
    ) {
      (errors as $TSFixMe).tenant = intl.formatMessage({
        id: "validation.manufacturer.no_tenant_selected",
      });
    }

    if (isEmpty(values.serial_number)) {
      (errors as $TSFixMe).serial_number = intl.formatMessage({
        id: "validation.serial_number.mandatory",
      });
    } else if (values.serial_number.trim().length < 4) {
      (errors as $TSFixMe).serial_number = intl.formatMessage({
        id: "validation.serial_number.too_short",
      });
    } else if (values.serial_number.length > 255) {
      (errors as $TSFixMe).serial_number = intl.formatMessage({
        id: "validation.serial_number.too_long",
      });
    }

    if (!values.product || isEmpty(values.product.code)) {
      (errors as $TSFixMe).product = intl.formatMessage({
        id: "validation.product_code.mandatory",
      });
    }

    /* istanbul ignore next */
    if (
      !isSomething(values.gpsInterval) &&
      isSomething(gpsError) &&
      gpsError !== null &&
      values.gpsActive
    ) {
      (errors as $TSFixMe).gpsInterval = intl.formatMessage({ id: gpsError });
    } else if (!isSomething(values.gpsInterval) && values.gpsActive) {
      (errors as $TSFixMe).gpsInterval = intl.formatMessage({
        id: "validation.gps_interval.undefined",
      });
    }

    if (values.production_date) {
      if (!/^[\d]{4}(-[01][\d](-[0-3][\d])?)?$/.test(values.production_date)) {
        (errors as $TSFixMe).production_date = intl.formatMessage({
          id: "validation.production_date.invalid",
        });
      } else if (
        /^[\d]{4}-[01][\d]-[0-3][\d]$/.test(values.production_date) &&
        !Date.parse(values.production_date)
      ) {
        (errors as $TSFixMe).production_date = intl.formatMessage({
          id: "validation.production_date.invalid",
        });
      } else if (
        /^[\d]{4}-[01][\d]$/.test(values.production_date) &&
        !Date.parse(`${values.production_date}-01`)
      ) {
        (errors as $TSFixMe).production_date = intl.formatMessage({
          id: "validation.production_date.invalid",
        });
      }
    }

    const specificationErrors = validateSpecifications(
      intl,
      values,
      isUserProjectEngineer,
    );

    if (specificationErrors) {
      (errors as $TSFixMe).specifications = specificationErrors;
    }

    return errors;
  };

  const loadGPSErrors = (errorValue: $TSFixMe) => {
    setGPSError(errorValue);
  };

  const loadGPS = (formProps: $TSFixMe, gpsIntervalOptions: $TSFixMe) => {
    const { values } = formProps;
    return (
      <div id="gps-settings-group" className="gps-settings-group-items">
        <div>
          <h3>
            {intl.formatMessage({ id: "label.gps_settings" })}
            <AssetGPSSettingsHelp />
          </h3>
        </div>
        <div>
          {gpsError === "validation.gps_interval.less" ? (
            <SubClue
              // @ts-expect-error TS(2322) FIXME: Type '{ id: string; message: string; }' is not ass... Remove this comment to see the full error message
              id="gps-clue"
              message={intl.formatMessage({
                id: "validation.gps_interval.less",
              })}
            />
          ) : null}
        </div>
        <div className="gps-settings-group-items-child">
          <SwitchInput
            {...formProps}
            id="gps-enabled"
            name="gpsActive"
            disabled={!gpsIntervalOptions}
          />
        </div>
        <div>
          <SelectBox
            {...formProps}
            id="gps-interval"
            name="gpsInterval"
            labelKey="name"
            label={intl.formatMessage({ id: "label.gps_interval" })}
            options={gpsIntervalOptions}
            disableOptionsSort
            disabled={!values.gpsActive || !gpsIntervalOptions}
            required
          />
        </div>
      </div>
    );
  };

  const renderTagButton = (
    formProps: $TSFixMe,
    showProceedToTagButton: $TSFixMe,
    proceedWithTag: $TSFixMe,
    productRules: $TSFixMe,
  ) => {
    if (!showProceedToTagButton) return null;
    const { isSubmitting, setFieldValue } = formProps;
    return (
      <SubmitButton
        id="asset-form-proceed-with-tag-button"
        fetching={
          isSubmitting &&
          (proceedWithTag || productRules.get("needsInstrumentation"))
        }
        disabled={
          !formProps.dirty || !canSubmit || (isSubmitting && !proceedWithTag)
        }
        text={intl.formatMessage({ id: "button.save_create_tag" })}
        onClick={() => setFieldValue("proceedWithTag", true)}
      />
    );
  };

  const renderConnectivitySettings = (
    formProps: $TSFixMe,
    showMeasuringInterval: $TSFixMe,
    showTransmissionInterval: $TSFixMe,
    showTimeOfFirstMeasurement: $TSFixMe,
    measuringIntervalOptions: $TSFixMe,
    transmissionIntervalOptions: $TSFixMe,
    gpsSettings: { enabled: $TSFixMe; intervalOptions: $TSFixMe },
  ) => (
    <div
      id="connectivity-settings-group"
      className="connectivity-settings-group-items"
    >
      <div>
        <h3>{intl.formatMessage({ id: "label.connectivity_settings" })}</h3>
      </div>
      {showMeasuringInterval ? (
        <SelectBox
          {...formProps}
          id="measuring-interval"
          name="measuringInterval"
          labelKey="name"
          label={intl.formatMessage({ id: "label.measuring_interval" })}
          options={measuringIntervalOptions}
          disableOptionsSort
          required
        />
      ) : null}
      {showTransmissionInterval ? (
        <SelectBox
          {...formProps}
          id="transmission-interval"
          name="transmissionInterval"
          labelKey="name"
          label={intl.formatMessage({ id: "label.transmission_interval" })}
          options={transmissionIntervalOptions}
          disableOptionsSort
          required
        />
      ) : /* istanbul ignore next */ null}
      {showTimeOfFirstMeasurement ? (
        <DateTimeInput
          {...formProps}
          id="first-measurement"
          type="text"
          name="firstMeasurement"
          label={intl.formatMessage({ id: "label.first_measurement" })}
          showSelectTime
          showSelectDate
          required
        />
      ) : null}
      {gpsSettings.enabled
        ? loadGPS(formProps, gpsSettings.intervalOptions)
        : null}
      <div />
    </div>
  );

  const renderForm = (formProps: $TSFixMe) => {
    const { isSubmitting, values } = formProps;
    const manufacturerName = get(values, "manufacturer.name");
    const extendedOrderCode = get(values, "extendedOrderCode");
    const isPublicTenant = values.manufacturer?.tenantPublic;

    const productCode = getProductCodeForRules(
      get(values, "product.code"),
      extendedOrderCode,
    );
    const productRules = ProductRules.find(
      manufacturerName,
      productCode,
      isPublicTenant,
    );
    const showMeasuringInterval = productRules?.get("needsMeasuringInterval");
    const showTransmissionInterval = productRules?.get(
      "needsTransmissionInterval",
    );
    const showTimeOfFirstMeasurement = productRules?.get(
      "needsTimeOfFirstMeasurement",
    );
    const needsInstrumentation = productRules?.get("needsInstrumentation");
    const needsGPSInterval = productRules?.get("needsGPSInterval");
    const editMode = values?.id > 0 && !values?.proceedWithTag;
    const showSaveButton = editMode || !needsInstrumentation;
    const showProceedToTagButton = !editMode;
    const measuringIntervalOptions = showMeasuringInterval
      ? productRules?.get("measuringIntervalOptions", intl)
      : null;
    const transmissionIntervalOptions = showMeasuringInterval
      ? measuringIntervalOptions.find(
          (label: $TSFixMe) => label.id === values.measuringInterval?.id,
        )?.transmissionOptions
      : productRules?.get("transmissionIntervalOptions");
    const proceedWithTag = get(values, "proceedWithTag");
    const gpsAllIntervalOptions = productRules?.get("gpsIntervalOptions", intl);
    const gpsIntervalOptions = gpsAllIntervalOptions?.find(
      (label: $TSFixMe) => label.name === values.transmissionInterval?.name,
    )?.gpsOptions;
    // For asset edit only, if asset has an tenant assigned, we use the asset option endpoints, so the user can
    // also use master data from a tenant he has no direct access to.
    const useAssetOptionsWithAssetId =
      (initialValues as $TSFixMe)?.id &&
      (initialValues as $TSFixMe).tenant?.id &&
      values.tenant?.id === (initialValues as $TSFixMe).tenant?.id
        ? (initialValues as $TSFixMe)?.id
        : undefined;
    const basicSettings =
      showMeasuringInterval &&
      needsGPSInterval &&
      isSomething(values.transmissionInterval);
    const enableGPSSettings = productRules.get(
      "enableGPSForOrderCode",
      basicSettings,
      extendedOrderCode,
      gpsDevSettings,
    );

    return (
      <Form {...formProps}>
        <CompanyTypeahead
          {...formProps}
          id="manufacturer"
          name="manufacturer"
          label={intl.formatMessage({ id: "label.manufacturer" })}
          assetId={useAssetOptionsWithAssetId}
          disabled={!!initialValues?.ownership_claimed}
          required={!initialValues?.ownership_claimed}
          autoFocus
          canCreate
        />
        {values.manufacturer?.new &&
        (userAdminTenants as $TSFixMe)?.length > 1 ? (
          <TenantTypeahead
            {...formProps}
            id="tenant"
            name="tenant"
            assetId={(initialValues as $TSFixMe)?.id}
            label={intl.formatMessage({ id: "label.tenant" })}
            canCreate
          />
        ) : null}
        <TextInput
          {...formProps}
          id="serial-number"
          name="serial_number"
          label={intl.formatMessage({ id: "label.serial_number" })}
          /* eslint-disable camelcase */
          disabled={!!initialValues?.ownership_claimed}
          required={!initialValues?.ownership_claimed}
        />
        <TextArea
          {...formProps}
          id="description"
          name="description"
          label={intl.formatMessage({ id: "label.description" })}
        />
        <ProductTypeahead
          {...formProps}
          id="product"
          name="product"
          manufacturerId={values.manufacturer?.id}
          assetId={useAssetOptionsWithAssetId}
          isEndress={values.manufacturer?.name === "Endress+Hauser"}
          canCreate
          disabled={!!initialValues?.ownership_claimed}
          required={!initialValues?.ownership_claimed}
        />
        {values.product?.new ? (
          <ProductCategoryTypeahead
            {...formProps}
            id="product-category"
            name="product_category"
            label={intl.formatMessage({ id: "label.product_category" })}
            tenantId={values.tenant?.id}
          />
        ) : null}

        <AssetFormSynchronization
          {...formProps}
          id="asset-form-sync"
          userAdminTenants={userAdminTenants}
          showMeasuringInterval={showMeasuringInterval}
          showTransmissionInterval={showTransmissionInterval}
          showTimeOfFirstMeasurement={showTimeOfFirstMeasurement}
          gpsEnabled={enableGPSSettings}
          gpsIntervalOptions={gpsIntervalOptions}
          loadGPSErrors={loadGPSErrors}
          gpsError={gpsError}
        />

        {showAssetStatus ? (
          <AssetStatusSelect
            {...formProps}
            id="asset-status"
            name="status"
            label={intl.formatMessage({ id: "label.asset_status" })}
            assetId={useAssetOptionsWithAssetId}
            required
          />
        ) : null}

        <TextInput
          {...formProps}
          id="production-date"
          name="production_date"
          disabled={!!initialValues?.ownership_claimed}
          placeholder={intl.formatMessage({
            id: "assets.production_date_placeholder",
          })}
          label={intl.formatMessage({ id: "label.asset_production_date" })}
        />
        <SpecificationsInput
          {...formProps}
          specificationKeysEndpoint="/asset/specification_keys"
          isUserProjectEngineer={isUserProjectEngineer}
        />
        {showMeasuringInterval ||
        showTransmissionInterval ||
        showTimeOfFirstMeasurement
          ? renderConnectivitySettings(
              formProps,
              showMeasuringInterval,
              showTransmissionInterval,
              showTimeOfFirstMeasurement,
              measuringIntervalOptions,
              transmissionIntervalOptions,
              {
                enabled: enableGPSSettings,
                intervalOptions: gpsIntervalOptions,
              },
            )
          : null}
        <ButtonGroup>
          {showSaveButton ? (
            <SubmitButton
              id="asset-form-submit-button"
              fetching={isSubmitting && !proceedWithTag}
              disabled={
                !formProps.dirty ||
                !canSubmit ||
                (isSubmitting && proceedWithTag)
              }
            />
          ) : null}
          {renderTagButton(
            formProps,
            showProceedToTagButton,
            proceedWithTag,
            productRules,
          )}

          <CancelButton id="asset-form-cancel-button" fetching={isSubmitting} />
        </ButtonGroup>
      </Form>
    );
  };

  return (
    <Formik
      id="asset-form"
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={validateForm}
    >
      {renderForm}
    </Formik>
  );
}

AssetForm.defaultProps = defaultProps;

export default withApi(AssetForm);
